import { useState } from "react";
import { motion } from "framer-motion";
import { Flag, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

const CATEGORIES = [
  {
    id: "report",
    label: "Report",
    description: "Content, safety or rights issues — lands directly at report@in-itapp.com",
    icon: Flag,
    accent: "border-ink-ember/60 bg-ink-ember/10 text-ink-ember",
  },
  {
    id: "customer_service",
    label: "Customer service",
    description: "Help, feedback or partnerships — lands directly at info@in-itapp.com",
    icon: Mail,
    accent: "border-ink-cyan/60 bg-ink-cyan/10 text-ink-cyan",
  },
];

const inputCls =
  "w-full rounded-2xl bg-white/[0.04] border border-white/12 px-5 py-3.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-ink-coral/60 transition-colors duration-200";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", category: "customer_service", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setReference(res.data.reference);
      toast.success("Message sent — we'll get back to you shortly.");
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 sm:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-coral mb-4">CONTACT & REPORT</p>
          <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl mb-6">
            Talk to a <span className="text-brand-gradient">human.</span>
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-10 max-w-md">
            Every message you send here lands directly in our inbox — your name and email included, so we can actually answer you.
          </p>

          <div className="space-y-4">
            <div className="rounded-3xl border border-white/10 bg-ink-card/60 p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-ink-coral mt-0.5 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">HEADQUARTERS</p>
                <p className="text-white font-semibold">IN-Itapp</p>
                <p className="text-zinc-400 text-sm">Lodjurstråket 1, 417 51 Gothenburg, Sweden</p>
              </div>
            </div>
            <a href="mailto:info@in-itapp.com" data-testid="contact-info-email" className="rounded-3xl border border-white/10 bg-ink-card/60 p-6 flex items-start gap-4 hover:border-ink-cyan/50 transition-colors duration-200 block">
              <Mail className="w-5 h-5 text-ink-cyan mt-0.5 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">CUSTOMER SERVICE</p>
                <p className="text-white font-semibold">info@in-itapp.com</p>
                <p className="text-zinc-400 text-sm">General help, creator support, partnerships</p>
              </div>
            </a>
            <a href="mailto:report@in-itapp.com" data-testid="contact-report-email" className="rounded-3xl border border-white/10 bg-ink-card/60 p-6 flex items-start gap-4 hover:border-ink-ember/50 transition-colors duration-200 block">
              <Flag className="w-5 h-5 text-ink-ember mt-0.5 shrink-0" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">REPORT</p>
                <p className="text-white font-semibold">report@in-itapp.com</p>
                <p className="text-zinc-400 text-sm">Content moderation, safety, privacy rights</p>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="rounded-3xl border border-white/10 bg-ink-card/60 p-7 sm:p-9"
        >
          {reference ? (
            <div data-testid="contact-success-toast" className="h-full flex flex-col items-center justify-center text-center py-16">
              <CheckCircle2 className="w-14 h-14 text-ink-green mb-6" />
              <h3 className="font-display font-bold text-white text-2xl mb-3">You're in our inbox.</h3>
              <p className="text-zinc-400 max-w-sm mb-2">
                Your message was sent to {form.category === "report" ? "report@in-itapp.com" : "info@in-itapp.com"} with your name and email attached.
              </p>
              <p className="font-mono text-xs tracking-[0.25em] text-ink-coral mb-8">REFERENCE #{reference}</p>
              <button
                data-testid="contact-send-another-btn"
                onClick={() => { setReference(null); setForm({ name: "", email: "", category: "customer_service", subject: "", message: "" }); }}
                className="rounded-full border border-white/15 px-6 py-3 text-sm font-bold text-white hover:border-ink-coral/60 transition-colors duration-200"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    data-testid={`contact-category-${cat.id}`}
                    onClick={() => setForm({ ...form, category: cat.id })}
                    className={`rounded-2xl border p-4 text-left transition-all duration-200 ${
                      form.category === cat.id ? cat.accent : "border-white/12 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    <cat.icon className="w-4 h-4 mb-2" />
                    <span className="block text-sm font-bold">{cat.label}</span>
                    <span className="block text-[11px] mt-1 opacity-70 leading-snug">{cat.description}</span>
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" required maxLength={120} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={inputCls} />
              </div>
              <input data-testid="contact-subject-input" required maxLength={200} value={form.subject} onChange={set("subject")} placeholder="Subject" className={inputCls} />
              <textarea data-testid="contact-message-input" required maxLength={5000} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what's happening..." className={`${inputCls} resize-none`} />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] text-white font-bold text-sm px-7 py-4 flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {sending ? "Sending..." : `Send to ${form.category === "report" ? "report@in-itapp.com" : "info@in-itapp.com"}`}
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
